import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../../common/CustomButtons";
import theme from "../../../../assets/theme";
import { navigate } from "@reach/router";
import { Images } from "../../../../assets/images";
import { AppBar, Tab, Tabs, makeStyles } from "@material-ui/core";
import PropTypes from "prop-types";
import COLORS from "../../../../assets/Colors";
import { H2 } from "../../AboutUs/Styles";
import useCartCount from "../../../../hooks/useCartCount";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <StyledText style={{ height: "100%" }}>{children}</StyledText>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const StyledText = styled.article`
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
  overflow: auto;
`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  appBar: {
    background: "transparent",
    boxShadow: "none",
    color: COLORS.COLOR_DARK,
  },
  tabsContainer: {
    overflowX: "auto",
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE/Edge */,
    "&::-webkit-scrollbar": { display: "none" },
    padding: "8px 0",
  },
  tabsFlex: {
    minWidth: "max-content", // Prevents shrinking
  },
  tab: {
    minWidth: 100,
    padding: "10px 16px",
    textTransform: "none",
    fontSize: "14px",
    fontFamily: "openSans-bold, sans-serif",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("sm")]: {
      minWidth: 120,
      fontSize: "16px",
      padding: "12px 24px",
    },
  },
  indicator: {
    height: 4,
    borderRadius: "2px",
    backgroundColor: "#00818a",
  },
}));

const Offerings = (props) => {
  const [selected, setSelected] = useState("Online Dispute Resolution");
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const { scrollTick } = useCartCount();

  const section = props.props.location.hash.slice(1);

  useEffect(() => {
    if (!section) return;

    const nameMap = {
      "case-management": "our-offerings",
      odr: "our-offerings",
      transcription: "our-offerings",
      "our-offerings": "our-offerings",
    };

    const labelMap = {
      "case-management": "Case Management",
      odr: "ODR Platform Deployments",
      transcription: "Live Transcription",
      "our-offerings": "Online Dispute Resolution",
    };

    const matchedKey = Object.keys(labelMap).find((key) =>
      section.includes(key)
    );

    if (matchedKey) {
      setSelected(labelMap[matchedKey]);
    }

    const targetName = nameMap[section] || "our-offerings";
    const element = document.getElementsByName(targetName)[0];
    if (!element) return;

    requestAnimationFrame(() => {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 70;

      window.scrollTo({ top: y, behavior: "smooth" });
    });
  }, [section, scrollTick]);

  // Fixed: dependency array now includes 'section'

  const offerings = [
    "Online Dispute Resolution",
    "ODR Platform Deployments",
    "Live Transcription",
    "Case Management",
  ];
  const tabs = ["Negotiation", "Mediation", "Arbitration"];

  const contents = [
    {
      title: "Negotiation",
      content:
        "Solving a dispute through a process of bids and offers using technology, game theory and AI/ML to enable a quick and amicable solution.",
      img: Images.Negotiation,
      link: "/negotiation-rules",
      modelClause: "/negotiation-model-clause",
    },
    {
      title: "Mediation",
      content:
        "Resolution of a dispute through an iterative dialogue facilitated by a trained mediator with domain expertise to arrive at a settlement agreement.",
      img: Images.Mediation,
      link: "/mediation-rules",
      modelClause: "/mediation-model-clause",
    },
    {
      title: "Arbitration",
      content:
        "For more complex disputes, Arbitration follows a process where parties may engage lawyers and advisors to put forth their case to a panel of one or more trained arbitrators to arrive at an arbitral award. Arbitration can be Fast track or Regular.",
      img: Images.Arbitration,
      link: "/arbitration-rules",
      modelClause: "/arbitration-model-clause",
    },
  ];

  const inputs_data = [
    {
      title: "ODR Platform Deployments",
      content:
        "Our ODR platform supports flexible SaaS and on-premise deployments, with personalized workflows tailored to institutional requirements. We have deployed and supported ODR systems for leading arbitral institutions including IIAC, NPAC, and IAMC. The platform enables end-to-end dispute management, role-based access, audit trails, and compliance-ready architecture, ensuring security, reliability, and operational efficiency for arbitration and mediation proceedings.",
      img: Images.Saas,
    },
    {
      title: "Live Transcription",
      content:
        "IAMC Video conferencing facility comes bundled with our State-of-the-Art Transcription Services available in multiple languages. While the software returns a 90-95% accurate transcription in real time, IAMC's trained Transcript Editors provide a 99% accurate output with flexible TATs, to suit the customer needs. Our Transcription Services have been well received by Arbitrators and other International Court Rooms.",
      img: Images.Transcription,
    },
    {
      title: "Case Management",
      content:
        "Case Management & Tribunal Secretarial Services: Trained tribunal secretarial staff,also called Scrutiny Clerks are assigned to each and every case ensuring convenience to all the parties in the case, including Claimant, Respondent & Neutrals (Mediators /Arbitrators).",
      img: Images.CaseManagement,
    },
  ];

  return (
    <MainWrapper name="our-offerings">
      <H2 style={{ textAlign: "center", marginBottom: "30px" }}>
        Our Offerings
      </H2>
      <MainContainer>
        <Component>
          <ComponentOne>
            {offerings.map((item) => (
              <ComponentOnePrimary
                key={item}
                active={selected === item}
                onClick={() => setSelected(item)}
              >
                <ComponentOnePrimaryText active={selected === item}>
                  {item}
                </ComponentOnePrimaryText>
              </ComponentOnePrimary>
            ))}
          </ComponentOne>
          <ComponentTwo>
            {selected === "Online Dispute Resolution" ? (
              <>
                <ComponentTwoInnerOne>
                  <div className={classes.root}>
                    <AppBar className={classes.appBar} position="static">
                      <div className={classes.tabsContainer}>
                        <Tabs
                          value={value}
                          onChange={(_, newValue) => setValue(newValue)}
                          variant="scrollable"
                          scrollButtons="off"
                          aria-label="odr-tabs"
                          classes={{
                            flexContainer: classes.tabsFlex,
                            indicator: classes.indicator,
                          }}
                        >
                          {tabs.map((tab) => (
                            <Tab
                              key={tab}
                              label={tab}
                              className={classes.tab}
                            />
                          ))}
                        </Tabs>
                      </div>
                    </AppBar>
                  </div>
                </ComponentTwoInnerOne>

                <TabPanel value={value} index={value}>
                  <MetaContent key={contents[value].title}>
                    <MetaImage bgImage={contents[value].img} />
                    <MetaInsideContent>
                      <MetaTitle>{contents[value].title}</MetaTitle>
                      <MetaBody>{contents[value].content}</MetaBody>
                      <ButtonContainer>
                        <PrimaryCTAButton
                          onClick={() => navigate(contents[value].link)}
                        >
                          Know More
                        </PrimaryCTAButton>
                        <PrimaryOutlinedCTAButton
                          onClick={() => navigate(contents[value].modelClause)}
                        >
                          Model Clause
                        </PrimaryOutlinedCTAButton>
                      </ButtonContainer>
                    </MetaInsideContent>
                  </MetaContent>
                </TabPanel>
              </>
            ) : (
              <>
                {inputs_data
                  .filter((item) => item.title === selected)
                  .map((item) => (
                    <MetaContent key={item.title}>
                      <MetaImage bgImage={item.img} />
                      <MetaInsideContent>
                        <MetaTitle>{item.title}</MetaTitle>
                        <MetaBody>{item.content}</MetaBody>
                        <ButtonContainer>
                          <PrimaryCTAButton
                            width="auto"
                            onClick={() => {
                              const element =
                                document.getElementById("enquire-now");
                              if (element) {
                                const y =
                                  element.getBoundingClientRect().top +
                                  window.pageYOffset -
                                  70;
                                window.scrollTo({ top: y, behavior: "smooth" });
                              }
                            }}
                          >
                            Enquire Now
                          </PrimaryCTAButton>
                        </ButtonContainer>
                      </MetaInsideContent>
                    </MetaContent>
                  ))}
              </>
            )}
          </ComponentTwo>
        </Component>
      </MainContainer>
    </MainWrapper>
  );
};

export default Offerings;

const MainWrapper = styled.section`
  width: 100%;
  padding: 3rem 1rem;
  box-sizing: border-box;

  @media (min-width: 1200px) {
    padding: 4rem 2rem;
  }

  @media (min-width: 1600px) {
    padding: 5rem 3rem;
  }
`;

// const H2 = styled.h2`
//   font-family: openSans-SemiBold, sans-serif;
//   font-weight: 400;
//   font-size: clamp(1.75rem, 4vw, 2.5rem);
//   line-height: 1.2;
//   color: #293461;
//   text-align: center;
//   margin: 0 auto 2rem;
//   max-width: 100%;
// `;

const MainContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
`;

const Component = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: clamp(2rem, 4vw, 4rem);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
`;

const ComponentOne = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: clamp(280px, 25vw, 380px);
  min-width: 280px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    max-width: 100%;
    min-width: unset;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ComponentTwo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const ComponentOnePrimary = styled.button`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: clamp(1rem, 2vw, 1.25rem);
  background: ${(props) => (props.active ? "#00818a" : "#f5f5f5")};
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 129, 138, 0.2);
  }
`;

const ComponentOnePrimaryText = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: ${(props) => (props.active ? "#ffffff" : "#293461")};
  line-height: 1.2;
`;

const ComponentTwoInnerOne = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  border-radius: 0.75rem;
  gap: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 1.25rem;
  width: ${({ width }) => (width ? width : "100%")};

  @media (max-width: 768px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const MetaContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: clamp(1.5rem, 4vw, 2.5rem);
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

export const MetaImage = styled.div`
  width: 100%;
  max-width: clamp(300px, 35vw, 400px);
  min-width: 300px;
  aspect-ratio: 4 / 3;
  background: url(${(props) => props.bgImage}) center/cover no-repeat;
  border-radius: 1rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    max-width: 100%;
    min-width: unset;
    aspect-ratio: 16 / 9;
  }
`;

const MetaInsideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
  }
`;

const MetaTitle = styled.h3`
  font-family: "Open Sans", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: clamp(1.25rem, 2.5vw, 1.5rem);
  line-height: 1.2;
  color: #293461;
  margin: 0;
  width: 100%;
`;

const MetaBody = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-style: normal;
  font-size: clamp(1rem, 2vw, 1.125rem);
  line-height: 1.6;
  color: #293461;
  margin: 0;
  flex: 1;
  width: 100%;
`;
